// src/encounter/nursing-plan-view.tsx
// [PROD-2949] The nurse's Nursing plan block. Mounted at the top of the nurse's P section ("Nursing
// plan", frame v2), above the Wrap-up block's contents, only when `?ef=frameV2` and `?ef=planSave` are
// both on (the P section exists only in frame v2).
//
// REUSES THE ALLIED PLAN COMPONENTS: the same `PlanSectionBlock` card, save line and refusal notice
// as the allied-health Plan step, over the nursing-process section (./lib/nursing-plan.ts). One
// component family, two contents, one save path.
//
// INSIDE THE CARD (design desk, 5 Oct): the save line sits at the card header's right, and a refusal
// is a Banner at the top of the card. The card is titled "Today's plan", because the section around
// it is already "Nursing plan" (one-title rule).
//
// TERMINATES IN A PLAN. A nurse never orders: there is no order, prescription or medication control
// here, not even a disabled one. The server refuses a nursing plan from any persona but a nurse, and
// once it has refused, the fields are read-only (the page's draft passes `readOnly`).
//
// Presentational: values and handlers flow in via props (the page's `useNonPrescriberPlanDraft`).
import { ALLIED_PLAN_TERMINAL } from './lib/allied-health-plan';
import { NURSING_PLAN_SECTION } from './lib/nursing-plan';
import { PlanSectionBlock } from './allied-health-plan-view';
import { PlanSaveLine, PlanSaveNotice } from './plan-save-status';
import { LOADING_STATUS, type PlanSaveState } from './use-non-prescriber-plan-draft';
import { NursingProblems } from './nursing-problems-view';
import type { NursingProblem } from './lib/nursing-problems';

export interface NursingPlanViewProps {
  /** Current answers, keyed by the nursing section's field ids. */
  values?: Readonly<Record<string, string>>;
  /** Persist one field. Omitted → read-only. */
  onChange?: (fieldId: string, value: string) => void;
  /** A provider signed the encounter, or the role may not write this plan: a record, not a form. */
  readOnly?: boolean;
  saveState?: PlanSaveState;
  /** Frame v2 only: inside the note's "Today's plan" block — no card frame and no second "Today's plan"
   *  title (the block carries it). Absent → the card exactly as before. */
  inNote?: boolean;
  /** [PROD-3045] Frame v2 only: today's qualifying findings. In the note the block draws STRUCTURED
   *  nursing problems (./nursing-problems-view.tsx) — no free-text box in any state (L10). */
  findings?: readonly NursingProblem[];
}

export function NursingPlanView({ values, onChange, readOnly, saveState, inNote = false, findings }: NursingPlanViewProps) {
  const locked = readOnly === true || onChange === undefined;
  if (inNote) {
    // [PROD-3045] Frame v2 (inside the note's "Today's plan" block, which carries the one title): the
    // structured nursing problems, with the same save line and refusal notice as the card.
    return (
      <div className="flex flex-col gap-2 w-full" data-testid="nursing-plan" data-terminal={ALLIED_PLAN_TERMINAL} data-structured="true">
        <PlanSaveNotice state={saveState} />
        <PlanSaveLine state={saveState} />
        <NursingProblems
          findings={findings ?? []}
          values={values}
          onChange={onChange}
          locked={locked}
          loading={saveState?.status === LOADING_STATUS}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col w-full" data-testid="nursing-plan" data-terminal={ALLIED_PLAN_TERMINAL}>
      <PlanSectionBlock
        section={NURSING_PLAN_SECTION}
        values={values}
        onChange={onChange}
        locked={locked}
        notice={<PlanSaveNotice state={saveState} />}
        headerEnd={<PlanSaveLine state={saveState} />}
        inNote={inNote}
        untitled={inNote}
      />
    </div>
  );
}
